'use client';

import { useRef, useEffect, useLayoutEffect, useState } from 'react';

const useVimeoPlayer = (video) => {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPaused, setIsPaused] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [remountKey, setRemountKey] = useState(0);

  // Bump the key to force a fresh iframe + Player on Activity show, since
  // Vimeo keeps trying to pause itself otherwise.
  useLayoutEffect(() => {
    return () => {
      setIsLoaded(false);
      setIsPaused(true);
      setIsBuffering(false);
      setRemountKey((key) => key + 1);
    };
  }, []);

  useEffect(() => {
    if (!iframeRef.current || !video?.video_url) return;

    const loadPlayer = async () => {
      // Lazy-load the Vimeo Player SDK (will not block the video itself by the way)
      const Player = (await import('@vimeo/player')).default;

      // Why not react-player? We have to use the vimeo SDK directly because
      // react-player blinks the video several times when it loads.
      playerRef.current = new Player(iframeRef.current);

      playerRef.current.on('loaded', () => setIsLoaded(true));
      playerRef.current.on('ended', () => setIsPaused(true));
      playerRef.current.on('pause', () => setIsPaused(true));
      playerRef.current.on('play', () => setIsPaused(false));
      playerRef.current.on('volumechange', (e) => {
        setIsMuted(e.volume === 0 || e.muted);
      });
      playerRef.current.on('bufferstart', () => setIsBuffering(true));
      playerRef.current.on('bufferend', () => setIsBuffering(false));

      // Get the initial state of the video (depends on browser policy)
      playerRef.current.getPaused().then((paused) => {
        setIsPaused(paused);
      });
      playerRef.current.getMuted().then((muted) => {
        setIsMuted(muted);
      });
    };

    loadPlayer();

    return () => {
      // Deliberately not calling destroy() on the player because it removes
      // the iframe from the DOM which interferes with nextjs's Activity nav.
      playerRef.current = null;
    };
  }, [video, remountKey]);

  return {
    iframeRef,
    playerRef,
    isLoaded,
    isMuted,
    isPaused,
    isBuffering,
    remountKey
  };
};

export default useVimeoPlayer;
